import React, { useRef } from "react";

import { yupResolver } from "@hookform/resolvers/yup";
import { Container, Row, Col, Form, Button } from "react-bootstrap";
import { useForm, SubmitHandler, SubmitErrorHandler } from "react-hook-form";
import ReCAPTCHA from "react-google-recaptcha";
import * as yup from "yup";

import { alertActions } from "../../../features/alert/alertSlice";
import { useAppDispatch } from "../../../hooks/reduxHooks";
import { submitContact } from "../../../lib/api";
import { displayFormAlert } from "../../../lib/utils";
import { IContactFormData } from "../../../types/forms";
import classNames from "classnames";

export const loader = async () => {
    return null;
};

const Contact = (): React.JSX.Element => {
    const dispatch = useAppDispatch();
    const recaptcha = useRef<ReCAPTCHA>(null);

    const contactFormSchema = yup.object().shape({
        firstName: yup.string().required("First Name is required"),
        lastName: yup.string().required("Last Name is required"),
        email: yup
            .string()
            .required("Email is required")
            .email("Email is invalid"),
        comment: yup.string().required("Comment/Question is required"),
        recaptcha: yup.string().nullable(),
    });

    const verifyCaptcha = (): boolean => {
        const recaptchaValue = recaptcha.current?.getValue();
        if (!recaptchaValue) {
            setError("recaptcha", {
                type: "custom",
                message: "Please verify the reCAPTCHA!",
            });
            return false;
        } else {
            clearErrors("recaptcha");
            return true;
        }
    };

    const {
        register,
        handleSubmit,
        setError,
        clearErrors,
        setValue,
        formState: { errors },
    } = useForm<IContactFormData>({
        mode: "onChange",
        resolver: yupResolver(contactFormSchema),
    });

    const onSubmit: SubmitHandler<IContactFormData> = async data => {
        if (verifyCaptcha()) {
            await submitContact(data)
                .then(() => {
                    dispatch(
                        alertActions.success({
                            text: "Thank you for reaching out to us. We will respond as soon as possible.",
                        }),
                    );
                })
                .catch(e => {
                    dispatch(
                        alertActions.error({
                            title: `The server encountered a problem sending your contact request.`,
                            text: "If the problem continues, please try emailing us directly.",
                            dismissible: true,
                        }),
                    );
                });
        }
    };

    const onError: SubmitErrorHandler<IContactFormData> = (): void => {
        verifyCaptcha();
    };

    const onChangeRecaptcha = async (token: string | null): Promise<void> => {
        const recaptchaValue = recaptcha.current?.getValue();
        setValue("recaptcha", recaptchaValue);
        verifyCaptcha();
        // TODO: Verify response by sending token to back-end API
    };

    return (
        <div data-testid="contactContent">
            <Container className={classNames("pt=2", "pb-5", "contact")}>
                <Form
                    onSubmit={handleSubmit(onSubmit, onError)}
                    id="contactForm"
                >
                    <Row>
                        <Form.Label>Name *</Form.Label>
                        <Form.Group as={Col} controlId="firstName">
                            <Form.Control
                                type="text"
                                {...register("firstName", {
                                    required: "First Name is required",
                                })}
                                aria-invalid={
                                    errors.firstName ? "true" : "false"
                                }
                            />
                            <Form.Label>First</Form.Label>
                            {errors.firstName &&
                                displayFormAlert({
                                    level: "danger",
                                    text: errors.firstName.message as string,
                                })}
                        </Form.Group>
                        <Form.Group as={Col} controlId="lastName">
                            <Form.Control
                                type="text"
                                {...register("lastName", {
                                    required: "Last Name is required",
                                })}
                                aria-invalid={
                                    errors.lastName ? "true" : "false"
                                }
                            />
                            <Form.Label>Last</Form.Label>
                            {errors.lastName &&
                                displayFormAlert({
                                    level: "danger",
                                    text: errors.lastName.message as string,
                                })}
                        </Form.Group>
                    </Row>
                    <Row>
                        <Form.Label>Email *</Form.Label>
                        <Form.Group as={Col} controlId="email">
                            <Form.Control
                                type="email"
                                {...register("email", {
                                    required: "Email address  is required",
                                })}
                                aria-invalid={errors.email ? "true" : "false"}
                            />
                            <Form.Label>Enter Email</Form.Label>
                            {errors.email &&
                                displayFormAlert({
                                    level: "danger",
                                    text: errors.email.message as string,
                                })}
                        </Form.Group>
                    </Row>
                    <Row>
                        <Form.Label>Comment *</Form.Label>
                        <Form.Group as={Col} controlId="comment">
                            <Form.Control
                                as="textarea"
                                rows={10}
                                cols={50}
                                {...register("comment")}
                            />
                        </Form.Group>
                    </Row>
                    <Row>
                        <Col xs={3}>
                            <Form.Label>CAPTCHA</Form.Label>
                            <ReCAPTCHA
                                ref={recaptcha}
                                sitekey={
                                    process.env.RECAPTCHA_SITE_KEY as string
                                }
                                onChange={onChangeRecaptcha}
                            />
                            {errors.recaptcha &&
                                displayFormAlert({
                                    level: "danger",
                                    text: errors.recaptcha.message as string,
                                })}
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Button variant="primary" type="submit">
                                Submit
                            </Button>
                        </Col>
                    </Row>
                </Form>
            </Container>
        </div>
    );
};

export default Contact;
